import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import PurchaseModal from "@/pages/marketplace/PurchaseModal";

const MODES = [
  { value: "add_sum", label: "Aggiungi e somma prezzi", hint: "Le pratiche vengono collegate e i prezzi sommati in un totale di gruppo" },
  { value: "add_separate", label: "Aggiungi come acquisto separato", hint: "Collegate ma i prezzi restano indipendenti" },
  { value: "replace", label: "Sostituisci e annulla la pratica corrente", hint: "La vecchia pratica viene annullata e rimborsata dopo la creazione della nuova" },
];

export function PracticeChangeDialog({ practice: p, open, onClose, onDone }) {
  const [mode, setMode] = useState("add_separate");
  const [serviceId, setServiceId] = useState("");
  const [services, setServices] = useState([]);
  const [pro, setPro] = useState(null);
  const [properties, setProperties] = useState([]);
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!open) return;
    setMode("add_separate"); setServiceId(""); setSelected(null); setQ("");
    api.get("/services").then((r) => setServices((r.data || []).filter((s) => s.is_active !== false))).catch(() => {});
    api.get(`/users/${p.professional_id}`).then((r) => setPro(r.data)).catch(() => setPro(null));
    api.get(`/properties?owner_id=${p.professional_id}`).then((r) => setProperties(r.data || [])).catch(() => {});
  }, [open, p.professional_id]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return t ? services.filter((s) => (s.name || "").toLowerCase().includes(t)) : services;
  }, [services, q]);

  const proceed = () => {
    const svc = services.find((s) => s._id === serviceId);
    if (!svc) { toast.error("Scegli il nuovo servizio"); return; }
    setSelected(svc);
  };

  const openIntent = useMemo(() => ({
    form: {
      property_id: p.property_id || "",
      is_shared_with_client: !!p.is_shared_with_client,
      client_name: p.client_name || "",
      client_email: p.client_email || "",
    },
  }), [p]);

  if (selected && pro) {
    return (
      <PurchaseModal
        selected={selected}
        services={services}
        properties={properties}
        user={{
          id: pro.id || pro._id, _id: pro.id || pro._id, role: "professional",
          email: pro.email, first_name: pro.first_name, last_name: pro.last_name,
          fascia_id: pro.fascia_id, parent_user_id: pro.parent_user_id,
          plafond_monthly_limit: pro.plafond_monthly_limit,
          plafond_used_current_month: pro.plafond_used_current_month,
        }}
        onBehalfOfProId={p.professional_id}
        openIntent={openIntent}
        extraPayload={{ replaces_practice_id: p._id, change_mode: mode }}
        onClose={() => { setSelected(null); onClose(); onDone(); }}
        onPropertiesChanged={async () => {
          try { const r = await api.get(`/properties?owner_id=${p.professional_id}`); setProperties(r.data || []); } catch {}
        }}
        onQuotesChanged={() => {}}
        onPurchaseSuccess={() => {}}
      />
    );
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="practice-change-dialog">
        <DialogHeader><DialogTitle>Cambia pratica</DialogTitle></DialogHeader>
        <div className="space-y-2">
          {MODES.map((m) => (
            <label key={m.value} className={`flex gap-2 p-3 rounded-md border cursor-pointer ${mode === m.value ? "border-indigo-400 bg-indigo-50/50" : ""}`} data-testid={`practice-change-mode-${m.value}`}>
              <input type="radio" name="change-mode" checked={mode === m.value} onChange={() => setMode(m.value)} className="mt-1" />
              <span>
                <span className="block text-sm font-semibold">{m.label}</span>
                <span className="block text-xs text-muted-foreground">{m.hint}</span>
              </span>
            </label>
          ))}
        </div>
        <div className="space-y-2 pt-2">
          <div className="text-sm font-medium">Nuovo servizio</div>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca servizio…" className="w-full h-9 border rounded-md px-2 text-sm" data-testid="practice-change-service-search" />
          <div className="max-h-48 overflow-y-auto border rounded-md divide-y">
            {filtered.map((s) => (
              <label key={s._id} className="flex items-center gap-2 px-2 py-1.5 text-sm cursor-pointer hover:bg-slate-50" data-testid={`practice-change-service-${s._id}`}>
                <input type="radio" name="change-svc" checked={serviceId === s._id} onChange={() => setServiceId(s._id)} />
                <span className="flex-1 truncate">{s.name}</span>
                <span className="text-xs text-muted-foreground">€ {Number(s.base_price || 0).toFixed(2)}</span>
              </label>
            ))}
            {filtered.length === 0 && <div className="p-2 text-xs text-muted-foreground">Nessun servizio</div>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="practice-change-cancel">Annulla</Button>
          <Button onClick={proceed} disabled={!serviceId || !pro} data-testid="practice-change-continue">Continua</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
